// Read-side parser for the documents lists the admin readers serve (BL-1476, BL-2957).
//
// Since BL-2957 the list is built by `lotDocumentItemsSql` / `auctionDocumentItemsSql` (`@auctogon/db/drizzle-helpers`)
// from the live attachment rows, so a reader receives an already-shaped array. This parser only narrows that `unknown`
// jsonb back to `ExternalDocument[]` for the repositories whose generic row mappers re-widen it, and normalizes a
// historical snake_case `storage_path` element. It builds NO address: a document opens through a short-lived signed
// link (`/api/lots|auctions/:id/documents/:documentId/download`), never a permanent `url`.
//
// Pure and client-safe: no `server-only`, no I/O, no gateway import.
import type { ExternalDocument } from '@auctogon/db/types/jsonb';

/**
 * Maximum number of external documents per lot — a generous guard against
 * runaway payloads. One source for the whole-array validator and the per-item
 * add (BL-2338), so the two doors cannot disagree on when a lot is full.
 */
export const MAX_EXTERNAL_DOCS_PER_LOT = 50;

/**
 * BUG-2061 — the longest document id and name a lot or auction stores. Documents
 * are bidder-visible JSONB, so an unbounded field bloats the row and the public
 * catalog payload. The id matches the media id cap; the name matches the staged
 * document ref's and the auction Media page's.
 */
export const MAX_DOCUMENT_ID_LENGTH = 200;
export const MAX_DOCUMENT_NAME_LENGTH = 255;

/**
 * Narrow a raw `documents` jsonb value to {@link ExternalDocument} records, in stored order.
 *
 * An entry with no resolvable `storagePath` has nothing to open and is dropped. Malformed (null / non-object) elements
 * are skipped rather than thrown on, so a stray element can never turn a lot's GET into a 500. The result carries no
 * `url` (BL-2957).
 *
 * @param raw the jsonb value (unknown shape)
 * @returns typed records in stored order
 * @sideEffect none: pure
 */
export function parseExternalDocuments(raw: unknown): ExternalDocument[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): ExternalDocument[] => {
    if (!entry || typeof entry !== 'object') return [];
    // eslint-disable-next-line responsecast/no-response-type-erasure -- JSONB narrow: widening a genuinely `unknown` array element to probe the canonical ExternalDocument fields; not erasing a typed DTO.
    const d = entry as Record<string, unknown>;
    // Narrowed by TYPE, not by `??` alone: collapses null, undefined and a non-string legacy value to the absent case.
    const rawPath = d.storagePath ?? d.storage_path;
    const storagePath = typeof rawPath === 'string' && rawPath !== '' ? rawPath : undefined;
    if (storagePath === undefined) return [];
    return [
      {
        // BL-2955: an entry with no `id` is addressed by its `storagePath`, the id the document links match.
        id: typeof d.id === 'string' ? d.id : storagePath,
        name: d.name as string,
        type: d.type as ExternalDocument['type'],
        uploadedAt: d.uploadedAt as string,
        storagePath,
        // BL-961's auction->lot fan-out flag, carried only when the row states it.
        ...(typeof d.appliesToAllLots === 'boolean' ? { appliesToAllLots: d.appliesToAllLots } : {}),
      },
    ];
  });
}

/** A stored document resolved by id, for opening through a short-lived link (BL-2955). */
export interface StoredDocumentRef {
  id: string;
  name: string;
  storagePath: string;
  /** Auction documents only: false opts the document out of its auction's lot pages (BL-961). */
  appliesToAllLots: boolean;
}

/**
 * Find one document in a raw `documents` JSONB blob by the id the clients hold
 * (BL-2955). The id is the entry's own `id`, else its `storagePath`, the same
 * fallback the bidder projection applies, so the id on the wire always finds the entry.
 * An entry with no usable `storagePath` has nothing to open and never matches.
 *
 * The one place both apps' document-link lookups match an id. BL-2957 replaces the
 * JSONB read behind it with the attachment rows and keeps this contract.
 *
 * @param raw the JSONB value (unknown shape)
 * @param documentId the id to find
 * @returns the entry's locator, or `null` when no entry carries that id
 * @sideEffect none: pure
 */
export function findStoredDocumentById(raw: unknown, documentId: string): StoredDocumentRef | null {
  if (!Array.isArray(raw)) return null;
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    // eslint-disable-next-line responsecast/no-response-type-erasure -- JSONB narrow: probing a genuinely `unknown` array element.
    const d = entry as Record<string, unknown>;
    const rawPath = d.storagePath ?? d.storage_path;
    const storagePath = typeof rawPath === 'string' && rawPath !== '' ? rawPath : null;
    const name = typeof d.name === 'string' ? d.name : null;
    if (storagePath == null || name == null) continue;
    const id = typeof d.id === 'string' ? d.id : storagePath;
    if (id !== documentId) continue;
    return { id, name, storagePath, appliesToAllLots: d.appliesToAllLots !== false };
  }
  return null;
}
